import type { JSX } from 'react';
import type { IActivity, IProcessAction, IProcessActivity, Process, ServiceWorkflowStep } from '@common/interfaces';
import { CustomControlMode } from '@common/enums';
import { PatientEpisodeCategoryCarePathContext } from '@frontend/components/pages/episodeCategory/carePath/patientEpisodeCategoryCarePathContext';
import { useDefinedContext } from '@frontend/utils/hooks/useDefinedContext';
import { ProcessActivityContextProvider } from './ProcessActivityContextProvider';
import { ProcessActivityEdit } from './processActivityEdit/ProcessActivityEdit';
import { ProcessActivityRead } from './processActivityRead/ProcessActivityRead';

type ProcessActivityProps = Readonly<
    {
        process: Process;
        serviceWorkflowStep?: ServiceWorkflowStep;
    } & (
        | {
              processAction?: never;
              processActionActivity: IActivity;
              processActivity?: never;
          }
        | {
              processAction?: IProcessAction;
              processActionActivity?: never;
              processActivity: IProcessActivity;
          }
    )
>;

export function ProcessActivity(props: ProcessActivityProps): JSX.Element {
    const { process, processAction, processActivity, processActionActivity, serviceWorkflowStep } = props;
    const { customControlVersion } = processActionActivity ?? processActivity;
    const { customControlCode } = processActionActivity ?? processActivity.serviceActivity;
    const serviceActivity = processActivity
        ? processActivity.serviceActivity
        : { ...processActionActivity, isPrintEnabled: false };

    const { activeModifyActivity, activeProcessAction } = useDefinedContext(PatientEpisodeCategoryCarePathContext);

    let mode = CustomControlMode.read;
    if (
        !processActivity?.id ||
        (activeProcessAction &&
            activeProcessAction.id === processAction?.id &&
            activeProcessAction.processWorkflowStepId === processAction?.processWorkflowStepId) ||
        (activeModifyActivity && activeModifyActivity.id === processActivity?.id)
    ) {
        mode = CustomControlMode.edit;
    }

    return (
        <ProcessActivityContextProvider
            customControlCode={customControlCode}
            customControlVersion={customControlVersion}
            patientData={process.patientData}
            processAction={processAction}
            processActivity={processActivity}
            processData={process.processData}
            serviceActivity={serviceActivity}
            serviceCode={process.service.code}
            serviceWorkflowStep={serviceWorkflowStep}
        >
            {/* Add div with optional process activity id so that it can be scrolled to */}
            <div id={processActivity?.id}>
                {mode === CustomControlMode.read && processActivity && (
                    <ProcessActivityRead processActivity={processActivity} />
                )}
                {mode === CustomControlMode.edit && <ProcessActivityEdit />}
            </div>
        </ProcessActivityContextProvider>
    );
}
